import { Fragment, useMemo, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import {
  ChevronDownIcon,
  PauseIcon,
  PlayIcon,
  TrashIcon,
} from "@heroicons/react/outline";
import { CheckIcon, XIcon } from "@heroicons/react/solid";
import {
  Button,
  Menu,
  MenuTrigger,
  MenuContent,
  MenuItem,
  MenuSeparator,
} from "ui";

import AlertModal from "../AlertModal";
import { axiosApiInstance } from "../../pages/api/axios";
import { parseErrorsToArray } from "../../utils/parseErrors";
import { Row } from "react-table";
import { toast } from "react-hot-toast";

type SubscriptionBulkActionsProps = {
  selectedRows: Row<{ id: string }>[];
  showSuccessMessage: (options: {
    name?: string;
    message?: string;
    action?: string;
  }) => void;
  setBulkActionErrorIds: React.Dispatch<React.SetStateAction<string[]>>;
  refreshTable: () => void;
  bulkActionsPermissions: null | Record<string, boolean>;
};

type OpenModalType = "pause" | "resume" | "cancel" | "delete";

export default function SubscriptionBulkActions({
  selectedRows,
  refreshTable,
  setBulkActionErrorIds,
  showSuccessMessage,
  bulkActionsPermissions,
}: SubscriptionBulkActionsProps) {
  const [openModal, setOpenModal] = useState<OpenModalType | null>(null);

  const selectedIds = useMemo(
    () => selectedRows.map((row) => row.original.id),
    [selectedRows]
  );

  const doBulkAction = ({
    type,
    selected,
  }: {
    type: string;
    selected: string[];
  }) => {
    setBulkActionErrorIds([]);

    return axiosApiInstance
      .post(
        `${process.env.NEXT_PUBLIC_API_BASE}/d/subscriptions/bulk_action/`,
        {
          bulk_action: type,
          selected: selected,
        }
      )
      .catch((serverError) => {
        const [bulkActionError] = parseErrorsToArray(serverError);
        const error = bulkActionError as unknown as {
          message: string;
          subscriptions: string[];
        };
        // Bulk actions can pe applied to subset of selected ones
        const isActionAppliedForSomeIDs =
          error.subscriptions.length < selectedIds.length;
        toast.error(error.message);
        setBulkActionErrorIds(error.subscriptions || []);

        if (isActionAppliedForSomeIDs) {
          refreshTable();
        }

        return Promise.reject(error);
      });
  };

  return (
    <>
      <Menu>
        <MenuTrigger asChild>
          <Button variant="secondary" className="!text-info space-x-2">
            <span>Actions</span>
            <ChevronDownIcon className="text-info h-5 w-5" aria-hidden="true" />
          </Button>
        </MenuTrigger>

        <MenuContent>
          {bulkActionsPermissions?.["pause_selected"] && (
            <MenuItem onSelect={() => setOpenModal("pause")}>
              <PauseIcon className="mr-2 h-5 w-5" />
              Pause
            </MenuItem>
          )}
          {bulkActionsPermissions?.["resume_selected"] && (
            <MenuItem onSelect={() => setOpenModal("resume")}>
              <PlayIcon className="mr-2 h-5 w-5" />
              Resume
            </MenuItem>
          )}
          {bulkActionsPermissions?.["cancel_selected"] && (
            <>
              <MenuSeparator />
              <MenuItem onSelect={() => setOpenModal("cancel")}>
                <XIcon className="mr-2 h-5 w-5" />
                Cancel
              </MenuItem>
            </>
          )}
          {bulkActionsPermissions?.["delete_selected"] && (
            <>
              <MenuSeparator />
              <MenuItem
                variant="destructive"
                onSelect={() => setOpenModal("delete")}
              >
                <TrashIcon className="mr-2 h-5 w-5" />
                Delete
              </MenuItem>
            </>
          )}
        </MenuContent>
      </Menu>

      <AlertModal
        isOpen={openModal === "pause"}
        onClose={() => setOpenModal(null)}
        variant="warning"
        Icon={PauseIcon}
        title="Pause subscription"
        description={<>Are you sure you want to pause this subscription?</>}
        confirmLabel="Pause"
        onConfirm={() =>
          doBulkAction({ type: "pause_selected", selected: selectedIds }).then(
            () => {
              showSuccessMessage({
                message: `${selectedIds.length} Subscriptions paused successfully!`,
              });
              refreshTable();
            }
          )
        }
      />

      <AlertModal
        isOpen={openModal === "resume"}
        onClose={() => setOpenModal(null)}
        variant="success"
        Icon={PlayIcon}
        title="Resume subscription"
        description={<>Are you sure you want to resume this subscription?</>}
        confirmLabel="Resume"
        onConfirm={() =>
          doBulkAction({ type: "resume_selected", selected: selectedIds }).then(
            () => {
              showSuccessMessage({
                message: `${selectedIds.length} Subscriptions resumed successfully!`,
              });
              refreshTable();
            }
          )
        }
      />

      <CancelBulkAction
        isOpen={openModal === "cancel"}
        onClose={() => setOpenModal(null)}
        doAction={(type) =>
          doBulkAction({ type, selected: selectedIds }).then(() => {
            showSuccessMessage({
              message: `${selectedIds.length} Subscriptions ${
                type === "terminate_selected" ? "terminated" : "cancelled"
              } successfully!`,
            });
            refreshTable();
          })
        }
      />

      <AlertModal
        isOpen={openModal === "delete"}
        onClose={() => setOpenModal(null)}
        variant="danger"
        Icon={TrashIcon}
        title="Delete subscription"
        description={
          <>
            Are you sure you want to delete this subscription ? <br />
            All of this data will be removed from the dashboard.
          </>
        }
        confirmLabel="Delete"
        onConfirm={() =>
          doBulkAction({ type: "delete_selected", selected: selectedIds }).then(
            () => {
              showSuccessMessage({
                message: `${selectedIds.length} Subscriptions deleted successfully!`,
              });
              refreshTable();
            }
          )
        }
      />
    </>
  );
}

const CancelBulkAction = ({
  doAction,
  isOpen,
  onClose,
}: {
  doAction(type: string): void;
  isOpen: boolean;
  onClose(): void;
}) => {
  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog
        as="div"
        className="fixed inset-0 z-10 overflow-y-auto"
        onClose={onClose}
      >
        <div className="flex min-h-screen items-end justify-center px-4 pb-20 pt-4 text-center sm:block sm:p-0">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="bg-foundations-subtler/50 fixed inset-0 transition-opacity" />
          </Transition.Child>

          {/* This element is to trick the browser into centering the modal contents. */}
          <span
            className="hidden sm:inline-block sm:h-screen sm:align-middle"
            aria-hidden="true"
          >
            &#8203;
          </span>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enterTo="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <div className="bg-foundations-default inline-block overflow-hidden rounded-lg px-4 pb-4 pt-5 text-left align-bottom shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6 sm:align-middle">
              <div>
                <div className="bg-success-subtler mx-auto flex h-12 w-12 items-center justify-center rounded-full">
                  <CheckIcon
                    className="text-success h-6 w-6"
                    aria-hidden="true"
                  />
                </div>
                <div className="mt-3 text-center sm:mt-5">
                  <Dialog.Title
                    as="h3"
                    className="text-foundations-boldest text-lg font-medium leading-6"
                  >
                    Cancel subscription
                  </Dialog.Title>
                  <div className="mt-2">
                    <p className="text-foundations-subtle text-sm">
                      Choose when the cancellation would be applied
                    </p>
                  </div>
                </div>
              </div>
              <div className="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
                <Button
                  colorScheme="danger"
                  className="w-full justify-center text-base sm:col-start-2  sm:text-sm"
                  onClick={() => {
                    doAction("terminate_selected");
                    onClose();
                  }}
                >
                  Now
                </Button>
                <Button
                  variant="secondary"
                  // colorScheme="danger"
                  className="mt-3 w-full justify-center sm:col-start-1 sm:mt-0 sm:text-sm"
                  onClick={() => {
                    doAction("cancel_selected");
                    onClose();
                  }}
                >
                  By next payment
                </Button>
              </div>
            </div>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  );
};
